import { AccountantService, AuthService } from "../../services";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDropDownRHFWithLabel,
	EnhancedDropDownWithLabel,
	EnhancedSnackbar,
	IResponse,
	IServerError,
} from "@websential/cosmic-v20";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import React, { useEffect, useRef, useState } from "react";
import { convertFileToBase64, formatTimestamp, useNavigateToLogout } from "../../common/Common";
import { setSelectedAccountant, setSelectedAccountantName, setSelectedClient, signInUpdate } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { IAppState } from "../../store/reducers";
import { INITIAL_SNACKBAR_DATA } from "../../common/constants";
import { useNavigate } from "react-router-dom";

const useGetData = (parentId: string | null, bankId: string | null) =>
	useQuery({
		queryKey: ["uploaded-doc-data", parentId, bankId],
		queryFn: () => {
			return AuthService.getUploadedDocumetnsDetails(parentId, bankId);
		},
		refetchOnWindowFocus: false,
	});
export const UploadDocuments = () => {
	const [selectedFile, setSelectedFile] = useState<any>(null);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	// const id = useSelector((state: IAppState) => state.client.id);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [imageUploaded, setImageUploaded] = useState<boolean>(false);
	const [base64Logo, setBase64Logo] = useState<string | null>(null);
	const [ownerSameAsFirstAccount, setOwnerSameAsFirstAccount] = useState<any>(100);
	const userFirstName = useSelector((state: IAppState) => state.user.profile.firstName);
	const userLastName = useSelector((state: IAppState) => state.user.profile.lastName);
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [uploadedDocId, setUploadedDocId] = useState<string | null>(null);

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const navigateToLogout = useNavigateToLogout();
	const lastBankScreen = useSelector(
		(state: IAppState) => state.user.profile.lastBankScreen
	);

	const [parentId, setParentId] = useState<any>(null)
	const [accountantBankDetailsForPADData, setAccountantBankDetailsForPADData] = useState<any>(undefined)

	const { isPending, data: uploadedDocumentsDetails }: any = useGetData(
		accountantBankDetailsForPADData && accountantBankDetailsForPADData.parentId,
		accountantBankDetailsForPADData && accountantBankDetailsForPADData.bankId
	);

	useEffect(() => {
		if (uploadedDocumentsDetails) {
			if (uploadedDocumentsDetails.accFile) {
				// Add the prefix if it's missing
				const base64String = uploadedDocumentsDetails.accFile;
				if (base64String.startsWith("data:")) {
					setBase64Logo(base64String);
				} else {
					setBase64Logo(`data:image/jpeg;base64,${base64String}`);
				}
			}
		}
	}, [uploadedDocumentsDetails])


	useEffect(() => {
		if (lastBankScreen !== 300 && isAdminUser === 100) {
			setIsLoading(true);
			(async () => {
				try {
					const accountantResponse: any =
						await AccountantService.getAccountants({
							search_query: "",
							order_by: "acc_title",
							order_direction: "asc",
							page: 1,
							size: 25,
						}, isAdminUser);
					if (
						accountantResponse &&
						accountantResponse.items.length === 1
					) {
						dispatch(setSelectedAccountant(accountantResponse.items[0]));
						dispatch(setSelectedAccountantName(accountantResponse.items[0]));
						setParentId(accountantResponse.items[0].id);
						(async () => {
							try {
								// setIsLoading(true)
								const accountantBankDetailsForPAD: any =
									await AccountantService.getAccountantBankDetailsForPAD(
										accountantResponse.items[0].id,
										isAdminUser
									);
								if (accountantBankDetailsForPAD) {
									setAccountantBankDetailsForPADData(accountantBankDetailsForPAD)
									formMethods.setValue('bankName', accountantBankDetailsForPAD.bankName)
									formMethods.setValue('branchAddress', accountantBankDetailsForPAD.branchAddress)
								}
							} catch { setIsLoading(false) } finally { setIsLoading(false) }

						})();
					} else {
						setIsLoading(false)
					}
				} catch { setIsLoading(false) }
			})();
		} else if (lastBankScreen !== 300 && isAdminUser === 200) {
			setIsLoading(true);
			(async () => {
				try {
					const clientResponse: any =
						await AccountantService.getClientByAccountantId(
							"",
							isAdminUser,
							{
								search_query: "",
								order_by: "client_name",
								order_direction: "asc",
								page: 1,
								size: 25,
							}, navigateToLogout);
					if (
						clientResponse &&
						clientResponse.items.length === 1
					) {
						dispatch(setSelectedClient(clientResponse.items[0]));
						setParentId(clientResponse.items[0].id);
						(async () => {
							try {
								const accountantBankDetailsForPAD: any =
									await AccountantService.getAccountantBankDetailsForPAD(
										clientResponse.items[0].id,
										isAdminUser
									);
								if (accountantBankDetailsForPAD) {
									setAccountantBankDetailsForPADData(accountantBankDetailsForPAD)
									formMethods.setValue('bankName', accountantBankDetailsForPAD.bankName)
									formMethods.setValue('branchAddress', accountantBankDetailsForPAD.branchAddress)
								}
							} catch { setIsLoading(false) } finally { setIsLoading(false) }
						})();
					} else {
						setIsLoading(false)
					}
				} catch { setIsLoading(false) }
			})();
		}
	}, [lastBankScreen])

	const onUploadLogo = (event: any) => {
		const file = event.target.files[0];
		setSelectedFile(file);
		if (file) {
			const reader = new FileReader();
			reader.onloadend = () => {
				const base64 = reader.result as string;
				setBase64Logo(base64);
				const cleanBase64 = base64.split(",")[1];
				cleanBase64;
			};
			reader.readAsDataURL(file);
		}
	};
	// const fileInputRef = useRef<HTMLInputElement>(null);
	const handleCancelLogo = () => {
		if (accountantBankDetailsForPADData && !accountantBankDetailsForPADData.uploadStatus) {
			setBase64Logo(null);
			setSelectedFile(null);
			formMethods.setValue('accFile', undefined);
			// if (fileInputRef.current) {
			// 	fileInputRef.current.value = '';
			// }
		} else {
			setDialogOpen(true);
			setSingleDelete(true);
			if (uploadedDocumentsDetails) {
				setUploadedDocId(uploadedDocumentsDetails.id);
			}
		}
	};

	const handleSubmit = (event: any) => {
		event?.preventDefault();
		const formData = new FormData();
		formData.append("accFile", selectedFile);

		let payload = {
			parentId: accountantBankDetailsForPADData && accountantBankDetailsForPADData.parentId,
			bankId: accountantBankDetailsForPADData && accountantBankDetailsForPADData.bankId,
			parentScreen: isAdminUser,
			formData,
		};
		console.log('payload', payload)
		console.log('formData', formData)
		// uploadDocumentDataFile(payload);
	};
	const onSubmit = async (formData: any) => {
		console.log('formData', formData)
		if (
			formData.accFile && formData.accFile.length === 0 || formData.accFile === undefined || formData.accFile === null
		) {
			setSnackbarData({
				open: true,
				message: "Please Choose File!",
				severity: "warning",
			});
		} else {
			let payload: any = {
				id: uploadedDocumentsDetails ? uploadedDocumentsDetails.id : undefined,
				parentId: accountantBankDetailsForPADData && accountantBankDetailsForPADData.parentId,
				bankId: accountantBankDetailsForPADData && accountantBankDetailsForPADData.bankId,
				parentScreen: isAdminUser,
				// formData,
			};
			if (formData.accFile && formData.accFile.length > 0) {
				const file = formData.accFile[0];
				const base64 = await convertFileToBase64(file);
				const cleanBase64 = base64.split(",")[1];
				payload.accFile = cleanBase64;
			} else if (formData.accFile) {
				const file = formData.accFile;
				const base64 = await convertFileToBase64(file);
				const cleanBase64 = base64.split(",")[1];
				payload.accFile = cleanBase64;
			}

			console.log('payload', payload)
			uploadDocumentDataFile(payload);

		}
	}
	const userIdStore = useSelector((state: IAppState) => state.user.profile.userId);

	const { isPending: isSubmitting, mutate: uploadDocumentDataFile } =
		useMutation({
			mutationFn: AuthService.uploadDocumentData,
			onSuccess: (res: any | IServerError | IResponse) => {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// if (lastBankScreen && lastBankScreen !== 300) {
				// 	(async () => {
				// 		try {
				// 			setIsLoading(true)
				// 			const response = await AuthService.lastBankScreen(userIdStore, 300);  //Upload documents
				// 			dispatch(
				// 				signInUpdate({
				// 					user: { lastBankScreen: 300 },
				// 				})
				// 			);
				// 		} catch { setIsLoading(false) } finally { setIsLoading(true) }
				// 	})();
				// }
				(async () => {
					try {
						setIsLoading(true)
						const accountantBankDetailsForPAD: any =
							await AccountantService.getAccountantBankDetailsForPAD(
								parentId,
								isAdminUser
							);
						if (accountantBankDetailsForPAD) {
							setAccountantBankDetailsForPADData(accountantBankDetailsForPAD)
							formMethods.setValue('bankName', accountantBankDetailsForPAD.bankName)
							formMethods.setValue('branchAddress', accountantBankDetailsForPAD.branchAddress)
						}
					} catch { setIsLoading(false) } finally { setIsLoading(false) }
				})();
				return queryClient.invalidateQueries({
					queryKey: ["uploaded-doc-data"],
				});
			},
			onError: (err: any) => {
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
			},
		});

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const onDashboardButtonClickHandler = () => {
		if (lastBankScreen && lastBankScreen !== 300) {
			(async () => {
				try {
					setIsLoading(true)
					const response = await AuthService.lastBankScreen(userIdStore, 300);  //Upload documents
					dispatch(
						signInUpdate({
							user: { lastBankScreen: 300 },
						})
					);
				} catch { setIsLoading(false) } finally { setIsLoading(true) }
			})();
		}
	}

	const { isPending: isDeleting, mutate: deleteUploadedDocumentById } = useMutation({
		mutationFn: AuthService.deleteUploadedDocument,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the Departments fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				window.location.reload();
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteUploadedDocField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			deleteUploadedDocumentById(uploadedDocId);
			setDialogOpen(false);
		}
	};
	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	return (
		<>
			<BackdropCircularProgress
				open={isSubmitting || isLoading || isDeleting}
				testId="upload-document-backdrop"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteUploadedDocField}
				cancelActionHandler={onCloseHandler}
				testId="departments-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Box sx={{ width: "100%" }}>
						<Grid
							container
							rowSpacing={1}
							columnSpacing={{ xs: 1, sm: 2, md: 3 }}
						>
							<Grid size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }} sx={{ marginTop: "1rem" }}>
								<Paper>
									<Box sx={{ padding: "16px" }}>
										<Grid container>
											<Grid
												size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
												sx={{ "& .MuiFormLabel-root": { marginBottom: "0.5rem" } }}
											>
												{/* when we a creating second account then show */}
												{accountantBankDetailsForPADData && accountantBankDetailsForPADData.totalPreviousBanks !== 0 && <EnhancedDropDownWithLabel
													selectName={"ownerSameAsFirstBankAccount"}
													items={[
														{
															label: "Yes",
															value: 200,
														},
														{
															label: "No",
															value: 100,
														},
													]}
													label={"Owner same as 1st bank account?"}
													testId={"Owner-same-as-1st-bank-account?"}
													selectedItem={ownerSameAsFirstAccount}
													onChangeHandler={(value: any) => setOwnerSameAsFirstAccount(value.value)}
												/>}
											</Grid>
											{ownerSameAsFirstAccount === 100 && <Grid
												size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
												sx={{ p: "1px" }}
											>
												<Box
													sx={{
														fontSize: "1rem !important",
														fontWeight: "bold",
														padding: "1.25rem 16px 16px 16px",
													}}
												>
													1. Account Owner ID
												</Box>
												<Box
													sx={{
														display: "flex",
														flexDirection: "row",
														alignItems: "center",
														gap: "1rem",
														p: "1px 12px",
													}}
												>
													<Typography
														sx={{
															color: "rgba(0,0,0,.6)",
															fontSize: ".875rem",
														}}
													>
														Clear and legible copy of valid photo ID with signature and full name
													</Typography>

													{base64Logo && (
														<div
															style={{
																border: "1px solid #000",
																borderRadius: "5px",
																width: "143px",
																height: "59px",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																overflow: "visible",        // allow icon to overflow
																backgroundColor: "#f9f9f9",
																flexShrink: 0,
																position: "relative",       // needed for absolute positioning of icon
																marginLeft: "auto"
															}}
														>
															<img
																src={base64Logo}
																alt=""
																style={{ width: "143px", height: "50px", objectFit: "contain" }}
															/>
															<div
																style={{
																	position: "absolute",   // positions relative to parent div
																	bottom: "-10px",
																	right: "-14px",
																}}
															>
																{/* {accountantBankDetailsForPADData && !accountantBankDetailsForPADData.uploadStatus && <EnhancedButtonPrimeReact
																	icon="pi pi-times"
																	severity={EnhancedButtonPrimeReactSeverity.Danger}
																	className="p-button-rounded p-button-text shadow-none bg-transparent"
																	rounded
																	onClickHandler={handleCancelLogo}
																/>} */}
																<EnhancedButtonPrimeReact
																	icon="pi pi-times"
																	severity={EnhancedButtonPrimeReactSeverity.Danger}
																	className="p-button-rounded p-button-text shadow-none bg-transparent"
																	rounded
																	onClickHandler={handleCancelLogo}
																/>
															</div>
														</div>
													)}

												</Box>

												{accountantBankDetailsForPADData && !accountantBankDetailsForPADData.uploadStatus ? <Box sx={{ p: "0px 12px", mt: 3 }}>
													<input
														id="accFile"
														{...formMethods.register("accFile")}
														// ref={fileInputRef}
														accept=".jpg, .png, .pdf, doc, .docx"
														type="file"
														onChange={onUploadLogo}
														style={{ color: "rgba(0,0,0,.6)" }}
													/>
												</Box> : uploadedDocumentsDetails && <span style={{ fontSize: ".875rem", color: "#ff9f00", fontWeight: "bold", paddingLeft: "12px" }}>
													Document saved at {uploadedDocumentsDetails && formatTimestamp(uploadedDocumentsDetails.createdAt)} by {userFirstName} {userLastName} ({uploadedDocumentsDetails && uploadedDocumentsDetails.createdByEmail})
												</span>}
											</Grid>}
										</Grid>

										<Grid
											size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
											sx={{
												display: "flex",
												justifyContent: "center",
												gap: "1rem",
												marginRight: "1rem",
												marginTop: "5rem"
											}}
										>
											{accountantBankDetailsForPADData && !accountantBankDetailsForPADData.uploadStatus && <EnhancedButtonPrimeReact
												label={ownerSameAsFirstAccount === 100 ? "Upload" : "Update"}
												type="submit"
												raised
												severity={EnhancedButtonPrimeReactSeverity.Success}
											/>}
											<EnhancedButtonPrimeReact
												label="Go To Dashboard"
												raised
												severity={EnhancedButtonPrimeReactSeverity.Secondary}
												onClickHandler={onDashboardButtonClickHandler}
												disabled={isLoading || accountantBankDetailsForPADData && !accountantBankDetailsForPADData.uploadStatus}
											/>
											{/* when we a creating second account then show */}
											{accountantBankDetailsForPADData && accountantBankDetailsForPADData.totalPreviousBanks !== 0 && <EnhancedButtonPrimeReact
												label="Pause"
												raised
												severity={EnhancedButtonPrimeReactSeverity.Danger}
												onClickHandler={onDashboardButtonClickHandler}
												disabled={!imageUploaded}
											/>}
										</Grid>
									</Box>
								</Paper>
							</Grid>
						</Grid>
					</Box>
				</form>
			</FormProvider>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"resource-list-snackbar"}
				open={snackbarData.open}
			/>
		</>
	);
};
